import { useMemo, useEffect, useState, useCallback } from "react";
import { CheckboxGroup, Form, FormInstance, Icon, Icons, Select, useToast, SelectOption, Spin } from "@ir/ui";
import {
  DisputeDataForSubmit,
  DisputeDocument,
  RaiseDisputeFormValues,
  RaiseDisputeModalProps,
} from "./RaiseDisputeInterface";
import { DisputeType, IsDocumentsRequiredConfig } from "@/core/repositories/deduction";
import { GetDisputeConfigDTO } from "@/core/dto";
import { DeductionDisputeRepository } from "@/infrastructure/repositories/deduction/DeductionDispute.repository";
import { GetDisputeConfigUseCase } from "@/core/usecases/deduction";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { createLogger } from "@/infrastructure/services/logger";
import DisputeNotesField from "./DisputeNotesField";

const client = GraphQlAdapter.getInstance().getClient();

interface RaiseDisputeFormProps extends RaiseDisputeModalProps {
  disputeForm: FormInstance<RaiseDisputeFormValues>;
  onDisputeSubmit: (disputeData: DisputeDataForSubmit) => void;
  deductionInvoiceAmount: number;
  remittanceItemNetAmount: number | null;
}

const RaiseDisputeKeheForm = ({
  deduction,
  documents,
  disputeForm,
  onDisputeSubmit,
  deductionInvoiceAmount,
  remittanceItemNetAmount,
  checkNumber,
  claimNumber,
}: RaiseDisputeFormProps) => {
  const logger = useCallback(() => createLogger("RaiseDisputeKeheForm"), []);
  const [disputeTypes, setDisputeTypes] = useState<DisputeType[]>([]);
  const [isDisputeTypesLoading, setIsDisputeTypesLoading] = useState(false);
  const [isDocumentsRequiredConfig, setIsDocumentsRequiredConfig] = useState<IsDocumentsRequiredConfig[]>([]);
  const [portalKey, setPortalKey] = useState<string | null>(null);
  const [isKeheFormLoading, setIsKeheFormLoading] = useState(true);
  const [dataProviderType, setDataProviderType] = useState<string | null>(null);
  const [noteTemplate, setNoteTemplate] = useState<string | null>(null);
  const { error: errorToast } = useToast();

  const getDisputeTypes = (): SelectOption[] => {
    return disputeTypes?.map(type => ({
      label: type.disputeTypeName,
      value: type.disputeTypeId.toString(),
    }));
  };

  const getDocuments = (): SelectOption[] => {
    const compareDocuments = (a: DisputeDocument, b: DisputeDocument) => {
      if (a.deletable && !b.deletable) return 1;
      if (!a.deletable && b.deletable) return -1;
      return 0;
    };
    return [...documents].sort(compareDocuments).map(document => ({
      label: document.name,
      value: document.id.toString(),
    }));
  };

  const isDocumentsRequired = useMemo(() => {
    return (
      isDocumentsRequiredConfig.find(config => config.deductionTypeId === deduction.deductionTypeId)
        ?.isDocumentsRequired ?? false
    );
  }, [isDocumentsRequiredConfig, deduction.deductionTypeId]);

  useEffect(() => {
    const fetchDisputeTypes = async () => {
      try {
        setIsDisputeTypesLoading(true);
        const response = await new GetDisputeConfigUseCase(new DeductionDisputeRepository(client)).execute(
          new GetDisputeConfigDTO({
            purchaserId: deduction?.purchaserId,
          })
        );
        setDisputeTypes(response.disputeConfig.disputeTypes);
        setIsDocumentsRequiredConfig(response.disputeConfig.isDocumentsRequiredConfig);
        setPortalKey(response.disputeConfig.portalKey);
        setNoteTemplate(response.disputeConfig.noteTemplate);
        disputeForm.setFieldsValue({
          disputeRequestMessage: response.disputeConfig.noteTemplate ?? "",
        });
        setDataProviderType(response.disputeConfig?.dataProviderType);
      } catch (error) {
        logger().error("Error fetching dispute types", error);
        errorToast({
          content: "Failed to fetch dispute types",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsDisputeTypesLoading(false);
        setIsKeheFormLoading(false);
      }
    };
    fetchDisputeTypes();
  }, [deduction.id, disputeForm, errorToast, deduction?.purchaserId, logger]);

  useEffect(() => {
    if (disputeTypes?.length > 0) {
      const findDisputeTypeByDeductionValidityRcaIds = disputeTypes.find(type =>
        type.deductionValidityRcaIds?.some(id => deduction?.rcaIds?.includes(id))
      );
      const findDisputeTypeByDeductionSubTypeId = disputeTypes.find(
        type =>
          type.deductionTypeIds?.includes(deduction?.deductionSubTypeId) ||
          type.deductionTypeIds?.includes(deduction?.deductionTypeId)
      );

      if (findDisputeTypeByDeductionValidityRcaIds) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionValidityRcaIds.disputeTypeName,
          value: findDisputeTypeByDeductionValidityRcaIds.disputeTypeId?.toString(),
        });
      } else if (findDisputeTypeByDeductionSubTypeId) {
        disputeForm.setFieldValue("disputeType", {
          label: findDisputeTypeByDeductionSubTypeId.disputeTypeName,
          value: findDisputeTypeByDeductionSubTypeId.disputeTypeId?.toString(),
        });
      }
    }

    if (documents?.length > 0) {
      const sortedDocuments = [...documents];
      disputeForm.setFieldValue(
        "documentsIds",
        sortedDocuments.filter(document => !document.deletable).map(document => document.id.toString())
      );
    }
  }, [
    documents,
    disputeTypes,
    deductionInvoiceAmount,
    remittanceItemNetAmount,
    deduction?.rcaIds,
    deduction?.deductionSubTypeId,
    disputeForm,
    deduction?.deductionTypeId,
  ]);

  const handleSubmit = (values: RaiseDisputeFormValues) => {
    if (!portalKey) {
      return;
    }

    const disputeData: DisputeDataForSubmit = {
      portalKey: portalKey,
      disputeRequestMessage: values.disputeRequestMessage,
      disputeTypeId: Number(values.disputeType.value),
      disputeAmount: deductionInvoiceAmount,
      totalAmount: remittanceItemNetAmount ?? 0,
      documentIds: values.documentsIds?.map(Number),
      dataProviderType: dataProviderType ?? undefined,
    };
    onDisputeSubmit(disputeData);
  };

  return isKeheFormLoading ? (
    <Spin size="large" spinning={isKeheFormLoading} />
  ) : (
    <Form form={disputeForm} onFinish={handleSubmit}>
      <Form.Item
        name="disputeType"
        rules={[{ required: true, message: `Dispute Type is required.` }]}
        label="Dispute Type"
      >
        <Select
          maxMenuHeight={150}
          className="select-field"
          options={getDisputeTypes()}
          isLoading={isDisputeTypesLoading}
          isClearable={false}
        />
      </Form.Item>
      <DisputeNotesField
        form={disputeForm}
        deduction={deduction}
        checkNumber={checkNumber}
        invoiceNumber={claimNumber}
        noteTemplate={noteTemplate}
        fallbackDisputeAmount={deductionInvoiceAmount}
      />
      {isDocumentsRequired && (
        <Form.Item
          name="documentsIds"
          rules={[{ required: true, message: `Select at least one document.` }]}
          label="Document Selection"
        >
          <CheckboxGroup
            options={getDocuments()}
            size="medium"
            variant="group"
            color="primary"
            defaultValue={documents.filter(document => !document.deletable).map(document => document.id.toString())}
          />
        </Form.Item>
      )}
    </Form>
  );
};

export default RaiseDisputeKeheForm;
